import { useRef, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useDrag, useDrop } from "react-dnd";
import filterIcon from "../../assets/icons/filter-icon.svg";

const ReportTableHeader = ({
    header,
    table,
    columnFilter,
    reorderColumn,
    sorting,
    setSorting,
    getSortDetails,
    flexRender,
    filteredDataLength,
    setCheckColumnOrder,
    sortIcon,
    organizationsAccess,
    handleSsnDisplayMode,
    ssnDisplayMode,
    isFetchingReportData,
    data,
    ssnValues,
    setPopupVisible,
    setAccessorKey,
    setColumnName,
    setColumnId,
    setIsPopupPosition,
    setFilterValue,
    isPopupVisible,
    columnName,
    filterValue,
}) => {
    const { getState, setColumnOrder } = table;
    const { columnOrder } = getState();
    const popupContainerRef = useRef(null);

    useEffect(() => {
        if (setCheckColumnOrder) {
            setCheckColumnOrder(columnOrder);
        }
    }, [columnOrder, setCheckColumnOrder]);

    const { column } = header;
    const isColumnFiltered = columnFilter.some(
        (filter) => filter.columnName === header.column.columnDef.header
    );

    const [{ isDragging }, dragRef] = useDrag({
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
        item: () => column,
        type: "column",
    });

    const [, dropRef] = useDrop({
        accept: "column",
        drop: (draggedColumn) => {
            const newColumnOrder = reorderColumn(
                draggedColumn.id,
                column.id,
                columnOrder
            );
            setColumnOrder(newColumnOrder);
            if (newColumnOrder[0] === columnOrder[columnOrder.length - 1])
                scrollToHorizontal();
        },
    });
    // Memoize the SSN visibility toggle function to avoid unnecessary re-renders
    const toggleSSNVisibility = useCallback(() => {
        if (organizationsAccess?.SSNACCESS === "Partial") {
            handleSsnDisplayMode((ssnDisplayMode + 1) % 2);
        }
        if (organizationsAccess?.SSNACCESS === "Full") {
            handleSsnDisplayMode((ssnDisplayMode + 1) % 3);
        }
    }, [organizationsAccess?.SSNACCESS, ssnDisplayMode, handleSsnDisplayMode]);

    // Memoize the function for scrolling to the right
    const scrollToHorizontal = useCallback(() => {
        const tableContainer = document.querySelector(
            ".custom-table-container"
        );
        if (tableContainer) {
            tableContainer.scrollTo({
                left: tableContainer.scrollWidth - tableContainer.clientWidth,
                behavior: "smooth",
            });
        }
    }, []);

    // Memoize the SSN icon rendering based on the access mode
    const getSSNIcon = () => {
        const icons = {
            Partial: [
                <i
                    key="partial-0"
                    className="fa fa-eye mask-eye cursor-pointer ml-4p"
                    style={{ color: "#037cb7" }}
                    aria-label="Mask SSN"
                />,
                <i
                    key="partial-1"
                    className="fa fa-eye-slash full-eye cursor-pointer ml-4p"
                    aria-hidden="true"
                    aria-label="Hide SSN"
                />,
            ],
            Full: [
                <i
                    key="full-0"
                    className="fa fa-eye part-eye cursor-pointer report-ssn-icon ml-4p"
                    style={{ color: "#037cb7" }}
                    aria-label="Show partial SSN"
                />,
                <i
                    key="full-1"
                    className="fa fa-eye mask-eye cursor-pointer ml-4p"
                    aria-label="Mask SSN"
                />,
                <i
                    key="full-2"
                    className="fa fa-eye-slash full-eye cursor-pointer ml-4p"
                    style={{ color: "#037cb7" }}
                    aria-hidden="true"
                    aria-label="Hide SSN"
                />,
            ],
        };
        return icons[organizationsAccess?.SSNACCESS]?.[ssnDisplayMode] || null;
    };

    const togglePopup = useCallback(
        (accessorKey, columnName, columnid, event) => {
            const iconRect = event.target.getBoundingClientRect();
            setPopupVisible(true);
            setAccessorKey(accessorKey);
            setColumnName(columnName);
            setColumnId(columnid);
            const tableContainer = document.querySelector(
                ".custom-table-container"
            );
            let leftPosition = iconRect.left + window.scrollX;
            let topPosition = iconRect.bottom + window.scrollY - 100; // Position below the icon

            if (tableContainer) {
                const tableRect = tableContainer.getBoundingClientRect();

                // Get popup width dynamically
                let popupWidth = 400;

                const existingPopup =
                    document.querySelector(".filter-popup-box");
                if (existingPopup) {
                    popupWidth = existingPopup.offsetWidth;
                } else {
                    const estimatedWidth = Math.max(
                        250,
                        columnName.length * 8 + 90
                    );
                    popupWidth = Math.min(estimatedWidth, 400);
                }
                if (leftPosition + popupWidth > tableRect.right) {
                    leftPosition =
                        iconRect.left + window.scrollX - popupWidth - 10;
                }
                if (leftPosition < tableRect.left) {
                    leftPosition = tableRect.left + 10;
                }
                if (leftPosition + popupWidth > tableRect.right) {
                    leftPosition = tableRect.right - popupWidth - 10;
                }
            }

            // Set the popup position
            setIsPopupPosition({ top: topPosition, left: leftPosition });

            // Find the filter value for the selected column
            const existingFilter = columnFilter.find(
                (filter) => filter.accessorKey === accessorKey
            );

            // Prefill the filter value if it exists, otherwise set it to an empty string
            setFilterValue(existingFilter?.filterValue || "");
        },
        [columnFilter]
    );

    const FilterIcon = useMemo(
        () => (
            <button
                style={{
                    marginLeft: "2px",
                    cursor: "pointer",
                    position: "relative",
                    background: "none",
                    border: "none",
                }}
                ref={popupContainerRef}
                onClick={(e) =>
                    togglePopup(
                        header.column.columnDef.accessorKey,
                        header.column.columnDef.header,
                        header.column.columnDef.columnId,
                        e
                    )
                }
                aria-label={`Column filter for ${header.column.columnDef.header}`}

                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        togglePopup(
                            header.column.columnDef.accessorKey,
                            header.column.columnDef.header,
                            header.column.columnDef.columnId,
                            e
                        ); // Trigger the click action on Enter or Space
                    }
                }}
            >
                {isColumnFiltered && (
                    <i
                        className="fa-solid fa-circle-check"
                        style={{
                            color: "#5E9E42",
                            position: "absolute",
                            top: "-6px",
                            left: 7,
                        }}
                    />
                )}
                <img
                    src={filterIcon}
                    alt=""
                    style={{
                        filter:
                            isPopupVisible &&
                            header.column.columnDef.header === columnName
                                ? "invert(46%) sepia(27%) saturate(450%) hue-rotate(175deg) brightness(94%) contrast(95%)"
                                : "none",
                    }}
                />
            </button>
        ),
        [filterValue, isPopupVisible, isColumnFiltered]
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isPopupVisible &&
                !event.target.closest(".filter-popup-box") &&
                !event.target.closest("[data-filter-icon]")
            ) {
                setPopupVisible(false);
            }
        };
        if (isPopupVisible) {
            const firstFocusableElement = document.querySelector(".input-box");
            if (firstFocusableElement) {
                firstFocusableElement.focus();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isPopupVisible]);

    // Memoize the column header styles
    const columnHeaderStyles = useMemo(
        () => ({
            opacity: isDragging ? 0.5 : 1,
            minWidth: header.column.columnDef.minSize,
            width: header.getSize(),
            maxWidth: header.column.columnDef.maxSize || header.getSize(),
            paddingLeft: 8,
            paddingTop: 10,
            paddingRight: 15,
            position: "relative",
            borderBottom: "1px solid #cdcdcd",
            boxShadow: "0 6px 10px rgba(0, 0, 0, 0.1)",
            textAlign: "left",
            wordBreak: "break-all",
        }),
        [isDragging, header.getSize()]
    );

    const handleSortingChange = (header) => {
        const columnId = header.column.id;
        const accessorKey = header.column.columnDef.accessorKey;
        const isDateColumn = /(Date|date|Dt|dt)/.test(columnId);
        const sortMethod = isDateColumn ? "date" : "";

        const isSameColumn = sorting.id === columnId;
        const isDescending = sorting.desc === true;
        const isAscending = sorting.desc === false;

        let newSorting;

        if (isSameColumn) {
            if (isAscending) {
                // Change to descending
                newSorting = {
                    id: columnId,
                    desc: true,
                    accessorKey,
                    sortMethod, // Check for 'Date', 'date', 'Dt', or 'dt' (case-sensitive)
                };
            } else if (isDescending) {
                // Reset to initial state
                newSorting = {
                    id: null,
                    desc: null,
                    accessorKey: null,
                };
            } else {
                // Change to ascending
                newSorting = {
                    id: columnId,
                    desc: false,
                    accessorKey,
                    sortMethod, // Check for 'Date', 'date', 'Dt', or 'dt' (case-sensitive)
                };
            }
        } else {
            // New column sorting - default to ascending
            newSorting = {
                id: columnId,
                desc: false,
                accessorKey,
                sortMethod, // Check for 'Date', 'date', 'Dt', or 'dt' (case-sensitive)
            };
        }

        setSorting(newSorting);
        getSortDetails(newSorting);

        // Accessibility announcement
        const sortOrder = newSorting.desc ? "descending" : "ascending";
        const announcement = `Table sorted by ${header.column.columnDef.header} in ${sortOrder} order.`;
        const liveRegion = document.getElementById("aria-live-region");
        if (liveRegion) {
            liveRegion.textContent = announcement;
            setTimeout(() => {
                liveRegion.textContent = "";
            }, 1000);
        }
    };

    return (
        <th ref={dropRef} colSpan={header.colSpan} style={columnHeaderStyles}>
            <div className="report-header d-flex">
                <div
                    ref={dragRef}
                    onClick={() => handleSortingChange(header)}
                    style={{
                        backgroundColor: "white",
                        wordBreak: "break-word",
                        textWrap: "nowrap",
                        textAlign: "left",
                    }}
                >
                    {header.isPlaceholder
                        ? null
                        : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                          )}
                    {filteredDataLength > 0 && (
                        /* sonar-ignore-start */
                        <>
                            {sorting.id !== header.column.id ? (
                                <button
                                    type="button"
                                    aria-label={`Sort by ${header.column.id}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSortingChange(header);
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        width: "20px",
                                        display: "inline-block",
                                        textAlign: "center",
                                    }}
                                >
                                    <img
                                        src={sortIcon}
                                        alt="" // Empty alt since aria-label is on the button
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : sorting.desc ? (
                                <button
                                    type="button"
                                    aria-label={`Sorted by ${header.column.id} in descending order`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSortingChange(header);
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        width: "20px",
                                        display: "inline-block",
                                        textAlign: "center",
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-arrow-down-wide-short"
                                        style={{ color: "#025780" }}
                                        aria-hidden="true"
                                    />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    aria-label={`Sorted by ${header.column.id} in ascending order`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSortingChange(header);
                                    }}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        padding: 0,
                                        cursor: "pointer",
                                        width: "20px",
                                        display: "inline-block",
                                        textAlign: "center",
                                    }}
                                >
                                    <i
                                        className="fa-solid fa-arrow-up-short-wide"
                                        style={{ color: "#025780" }}
                                    />
                                </button>
                            )}
                        </>
                        /* sonar-ignore-end */
                    )}
                </div>

                {(column.id === "taxIdentificationNumber" ||
                    column.id === "ssn") && (
                    /* sonar-ignore-start */
                    <div
                        onClick={toggleSSNVisibility}
                        role="button"
                        tabIndex={
                            !isFetchingReportData &&
                            data.length > 0 &&
                            ssnValues[0] !== "No Access"
                                ? 0
                                : -1
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleSSNVisibility();
                            }
                        }}
                        aria-label={
                            !isFetchingReportData &&
                            data.length > 0 &&
                            ssnValues[0] !== "No Access" &&
                            getSSNIcon()?.props?.["aria-label"]
                        }
                        style={{ cursor: "pointer" }}
                    >
                        {!isFetchingReportData &&
                            data.length > 0 &&
                            ssnValues[0] !== "No Access" &&
                            getSSNIcon()}
                    </div>
                    /* sonar-ignore-end */
                )}
                {filteredDataLength > 0 && FilterIcon}
            </div>

            <div
                onDoubleClick={header.getResizeHandler()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                role="button"
                aria-label={`Resize handle for ${header.column.columnDef.header}, use left/right arrow keys to resize column, finish resizing by moving focus away from handle`}
                aria-orientation="vertical"
                tabIndex={0}
                onKeyDown={(e) => {
                    const step = 10;

                    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                        e.preventDefault();
                        e.stopPropagation();

                        const currentSize = header.getSize();
                        const newSize =
                            e.key === "ArrowRight"
                                ? currentSize + step
                                : currentSize - step;

                        // Update column sizing state
                        table.setColumnSizing((old) => ({
                            ...old,
                            [header.column.id]: newSize,
                        }));
                    }
                }}
                style={{ cursor: "col-resize", border: "none" }}
            />
        </th>
    );
};

ReportTableHeader.propTypes = {
    header: PropTypes.object.isRequired,
    table: PropTypes.shape({
        getState: PropTypes.func.isRequired,
        setColumnOrder: PropTypes.func.isRequired,
    }).isRequired,
    columnFilter: PropTypes.arrayOf(PropTypes.object).isRequired,
    reorderColumn: PropTypes.func.isRequired,
    sorting: PropTypes.shape({
        id: PropTypes.string,
        desc: PropTypes.bool,
        accessorKey: PropTypes.string,
    }).isRequired,
    setSorting: PropTypes.func.isRequired,
    getSortDetails: PropTypes.func.isRequired,
    flexRender: PropTypes.func.isRequired,
    filteredDataLength: PropTypes.number.isRequired,
    setCheckColumnOrder: PropTypes.func,
    sortIcon: PropTypes.string.isRequired,
    organizationsAccess: PropTypes.shape({
        SSNACCESS: PropTypes.string,
    }),
    handleSsnDisplayMode: PropTypes.func.isRequired,
    ssnDisplayMode: PropTypes.number.isRequired,
    isFetchingReportData: PropTypes.bool.isRequired,
    data: PropTypes.array.isRequired,
    ssnValues: PropTypes.array.isRequired,
    setPopupVisible: PropTypes.func.isRequired,
    setAccessorKey: PropTypes.func.isRequired,
    setColumnName: PropTypes.func.isRequired,
    setColumnId: PropTypes.func.isRequired,
    setIsPopupPosition: PropTypes.func.isRequired,
    setFilterValue: PropTypes.func.isRequired,
    isPopupVisible: PropTypes.bool.isRequired,
    columnName: PropTypes.string,
    filterValue: PropTypes.string,
};

export default ReportTableHeader;
